import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { Button } from './Button';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  /** Hides the button entirely — never disables it. */
  permission?: string;
  disabled?: boolean;
}

/**
 * Text-first empty panel — reference §14.
 *
 * No illustration by design: art costs vertical space and adds nothing the sentence does
 * not already say. A search that matched nothing is the caller's own plain line, not this.
 *
 * Reads permissions from `useAuth`, so it must render inside the auth provider.
 */
export function EmptyState({
  message,
  action,
  minHeight = 'min-h-[280px]',
}: {
  /** Prose, or a node where the sentence needs a value in it. A string is a ReactNode. */
  message: ReactNode;
  action?: EmptyStateAction;
  minHeight?: string;
}) {
  const { hasPermission } = useAuth();
  const allowed = !action?.permission || hasPermission(action.permission);

  return (
    <div
      className={`flex ${minHeight} flex-col items-center justify-center gap-3 px-6 py-10 text-center`}
    >
      <p className="text-sm text-slate-500">{message}</p>

      {action && allowed ? (
        /*
          THE SHARED BUTTON, not a copy of its class string.

          This is the THIRD place the index page's Create renders — the toolbar and the
          selection bar are the other two, and they are one action, which is why
          `createDisabledReason` already reaches all three. It hand-rolled the whole primary
          surface instead, and on 29 Sep 2026 that cost exactly what a copy costs: the label
          went `slate-900` in `BUTTON_SURFACES` and this one stayed white, so one action would
          have rendered two ways on one screen.

          `buttonSurface.ts` says it outright — anything that is one button is `<Button>`, and
          the exported constants exist so a JOINED control can draw the paint, not so a caller
          can retype it.
        */
        <Button
          variant="primary"
          onClick={action.onClick}
          disabled={action.disabled}
          leading={action.icon ?? <Plus className="h-4 w-4" aria-hidden="true" />}
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
